import { useTranslation } from "react-i18next";

import { CheckIcon, FunnelIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface FacetedOptionLike {
  id: string | number;
  name: string;
}

type SingleProps<T extends FacetedOptionLike> = {
  mode: "single";
  title: string;
  options: T[];
  value: T | null;
  onChange: (v: T | null) => void;
};

type MultiProps<T extends FacetedOptionLike> = {
  mode: "multi";
  title: string;
  options: T[];
  value: T[];
  onChange: (v: T[]) => void;
  intersection?: boolean;
  onIntersectionChange?: (b: boolean) => void;
  /** Browse-mode loading: true while the cached list hasn't resolved yet. */
  isLoading?: boolean;
  /** Server-backed mode: controlled search + pinned selected (off-page selections). */
  searchable?: {
    query: string;
    onQueryChange: (q: string) => void;
    isLoading: boolean;
    selectedOptions: T[];
  };
};

export type DataTableFacetedFilterProps<T extends FacetedOptionLike> =
  SingleProps<T> | MultiProps<T>;

export function DataTableFacetedFilter<T extends FacetedOptionLike>(
  props: DataTableFacetedFilterProps<T>,
) {
  const { t } = useTranslation();
  const selectedCount =
    props.mode === "single" ? (props.value ? 1 : 0) : props.value.length;

  const isSelected = (opt: T) =>
    props.mode === "single"
      ? props.value?.id === opt.id
      : props.value.some((v) => v.id === opt.id);

  const toggle = (opt: T) => {
    if (props.mode === "single") {
      props.onChange(isSelected(opt) ? null : opt);
    } else {
      const next = isSelected(opt)
        ? props.value.filter((v) => v.id !== opt.id)
        : [...props.value, opt];
      props.onChange(next);
    }
  };

  const clear = () =>
    props.mode === "single" ? props.onChange(null) : props.onChange([]);

  // Multi-mode: derive selected and rest for pinning
  const renderMultiContent = () => {
    if (props.mode !== "multi") return null;

    const multiLoading = !!(props.searchable?.isLoading || props.isLoading);
    const searchable = props.searchable;

    const selectedOpts: T[] = searchable
      ? searchable.selectedOptions
      : props.value.filter((v) => props.options.some((o) => o.id === v.id));

    const selectedIds = new Set(selectedOpts.map((o) => o.id));
    const restOpts = props.options.filter((o) => !selectedIds.has(o.id));

    const renderRow = (opt: T) => {
      const sel = isSelected(opt);
      return (
        <CommandItem key={opt.id} onSelect={() => toggle(opt)}>
          <div
            className={cn(
              "border-primary mr-2 flex size-4 items-center justify-center rounded-sm border",
              sel
                ? "bg-primary text-primary-foreground"
                : "opacity-50 [&_svg]:invisible",
            )}
          >
            <CheckIcon className="size-3" />
          </div>
          <span>{opt.name}</span>
        </CommandItem>
      );
    };

    return (
      <>
        {selectedOpts.length > 0 && (
          <CommandGroup heading={t("components.entitySelect.selectedHeading")}>
            {selectedOpts.map(renderRow)}
          </CommandGroup>
        )}
        {selectedOpts.length > 0 && restOpts.length > 0 && <CommandSeparator />}
        {restOpts.length > 0 && (
          <CommandGroup>{restOpts.map(renderRow)}</CommandGroup>
        )}
        {multiLoading && (
          <div className="text-muted-foreground p-2 text-sm">
            {t("common.loading")}
          </div>
        )}
      </>
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 border-dashed"
          aria-label={props.title}
        >
          <FunnelIcon className="mr-1 size-4" />
          {props.title}
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2 px-1 font-normal">
              {selectedCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        {props.mode === "multi" && props.onIntersectionChange && (
          <div className="border-b p-1">
            <div className="grid grid-cols-2 gap-1">
              <Button
                size="sm"
                variant={props.intersection ? "ghost" : "secondary"}
                onClick={() => props.onIntersectionChange!(false)}
              >
                {t("components.dataTable.filter.anyOf")}
              </Button>
              <Button
                size="sm"
                variant={props.intersection ? "secondary" : "ghost"}
                onClick={() => props.onIntersectionChange!(true)}
              >
                {t("components.dataTable.filter.allOf")}
              </Button>
            </div>
          </div>
        )}
        <Command shouldFilter={props.mode !== "multi" || !props.searchable}>
          <CommandInput
            placeholder={props.title}
            {...(props.mode === "multi" && props.searchable
              ? {
                  value: props.searchable.query,
                  onValueChange: props.searchable.onQueryChange,
                }
              : {})}
          />
          <CommandList>
            {!(
              props.mode === "multi" &&
              !!(props.searchable?.isLoading || props.isLoading)
            ) && <CommandEmpty>{t("common.noResults")}</CommandEmpty>}
            {props.mode === "multi" ? (
              renderMultiContent()
            ) : (
              <CommandGroup>
                {props.options.map((opt) => {
                  const sel = isSelected(opt);
                  return (
                    <CommandItem key={opt.id} onSelect={() => toggle(opt)}>
                      <div
                        className={cn(
                          "border-primary mr-2 flex size-4 items-center justify-center rounded-sm border",
                          sel
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <CheckIcon className="size-3" />
                      </div>
                      <span>{opt.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {selectedCount > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={clear}
                    className="justify-center text-center"
                  >
                    {t("components.dataTable.filter.clear")}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
